import { useState } from "react";
import axios from "axios";
import {
  Box, Button, Link, Typography, CircularProgress, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { API_BASE_URL } from "../config";
import { downloadDocument } from "../download";

export const DOCUMENTS_API = `${API_BASE_URL}/documents`;

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Upload / view a single stored document.
 *
 * Props:
 *  - doc:         current document { id, original_filename } or null
 *  - onUploaded:  (doc) => void  — called with the new document after upload
 *  - onDeleted:   () => void     — called after the document is deleted; when
 *                                  omitted no delete button is shown
 *  - category:    string stored against the document (e.g. "cv")
 *  - personnelId: number | null  — associates the upload with a person
 *  - label:       noun used in button/empty text (e.g. "CV", "certificate")
 *  - required:    when true and empty, shows the empty message in an error colour
 *  - severity:    "error" | "warning" | null — colours the empty message (compliance highlight)
 */
export default function DocumentUpload({ doc, onUploaded, onDeleted, category, personnelId, label = "document", required = false, severity = null }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const handleDelete = () => {
    setBusy(true);
    setError(null);
    axios.delete(`${DOCUMENTS_API}/${doc.id}`)
      .then(() => { setConfirm(false); onDeleted && onDeleted(); })
      .catch(() => setError("Delete failed"))
      .finally(() => setBusy(false));
  };

  const handleFile = (files) => {
    const file = files && files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (typeof personnelId === "number") fd.append("personnel_id", String(personnelId));
    if (category) fd.append("category", category);
    setBusy(true);
    setError(null);
    axios.post(`${DOCUMENTS_API}/upload`, fd)
      .then(res => onUploaded(res.data))
      .catch(() => setError("Upload failed"))
      .finally(() => setBusy(false));
  };

  // Downloads under the original filename rather than opening a tab — see
  // download.js for why.
  const handleDownload = async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadDocument(doc);
    } catch {
      setError("Could not download document");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <Button variant="outlined" component="label" disabled={busy}
              startIcon={busy ? <CircularProgress size={16} /> : <UploadFileIcon />}>
        {doc ? `Replace ${label}` : `Upload ${label}`}
        <input hidden type="file" onChange={e => handleFile(e.target.files)} />
      </Button>
      {doc ? (
        <>
          <Link component="button" type="button" onClick={handleDownload} disabled={busy}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            {doc.original_filename} <DownloadIcon sx={{ fontSize: 14 }} />
          </Link>
          {onDeleted && (
            <IconButton size="small" color="error" disabled={busy}
                        aria-label={`Delete ${label}`} onClick={() => setConfirm(true)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </>
      ) : (
        <Typography variant="body2"
                    color={severity === "error" ? "error" : severity === "warning" ? "warning.main" : (required ? "error" : "text.secondary")}>
          {severity === "warning"
            ? `${cap(label)} missing`
            : (severity === "error" || required) ? `${cap(label)} required` : `No ${label} uploaded`}
        </Typography>
      )}
      {error && <Typography variant="body2" color="error">{error}</Typography>}

      <Dialog open={confirm} onClose={() => (busy ? null : setConfirm(false))}>
        <DialogTitle>Delete {label}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete {doc?.original_filename ? `"${doc.original_filename}"` : `this ${label}`}. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)} disabled={busy}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />}
                  onClick={handleDelete} disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
